import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, Clock, Loader2, RefreshCw, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import XeroMetricInfo, {
  type MetricDefinition,
} from "@/components/admin/XeroMetricInfo";
import { StatusBadge, TriggerStatusBadge } from "@/components/admin/XeroRunDisplay";
import { useToast } from "@/hooks/use-toast";
import { REASON_LABEL, REASON_TONE, isNoteworthy } from "@/lib/xeroReasonLabels";
import { formatNumber, formatWhen } from "@/lib/xeroRunFormat";
import { useXeroOrgName } from "@/hooks/useXeroOrgName";
import {
  XERO_PIPELINE_RUNS_QUERY_KEY,
  cancelXeroPipelineRun,
  isXeroRunInFlight,
  xeroPipelineRunQueryKey,
  type XeroPipelineRun,
} from "@/services/xeroApi";

const MODE_LABEL: Record<XeroPipelineRun["mode"], string> = {
  incremental: "Incremental",
  backfill: "Backfill",
};

const METRICS: MetricDefinition[] = [
  {
    term: "Organisations",
    definition:
      "Every organisation Xero currently grants. While a run is in flight this exceeds the number of rows below, because the notebook opens a row per organisation as it reaches it.",
  },
  {
    term: "Succeeded",
    definition: "Organisations the notebook has finished pulling.",
  },
  {
    term: "Failed",
    definition:
      "Organisations that raised an error. A failure never advances that organisation's watermark, so the next run re-requests what this one could not persist.",
  },
  {
    term: "In progress",
    definition:
      "Organisations with an open row — the notebook has started them and not yet reported a result.",
  },
  {
    term: "Rows",
    definition:
      "Journal rows written so far. This counts rows merged rather than rows added: an unchanged journal re-loaded is written and counted again.",
  },
  {
    term: "Pages",
    definition:
      "Pages of 100 journals fetched from Xero. Roughly one Xero call each, but it undercounts: retries and token rotations cost a call and are not counted here. The Request budget tab has the real figure.",
  },
  {
    term: "Watermark",
    definition:
      "The highest Xero journal number loaded for that organisation. The next run resumes above it.",
  },
];

interface Props {
  run: XeroPipelineRun;
  isFetching: boolean;
  onRefresh: () => void;
}


export default function XeroPipelineRunCard({ run, isFetching, onRefresh }: Props) {
  const orgName = useXeroOrgName();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const inFlight = isXeroRunInFlight(run.status);
  // Queued in Prism behind a run that was already going; not in Fabric yet.
  const waiting = run.status === "waiting";
  const active = inFlight || waiting;

  const cancel = useMutation({
    mutationFn: () => cancelXeroPipelineRun(run.run_id),
    onSuccess: (updated) => {
      queryClient.setQueryData(xeroPipelineRunQueryKey(run.run_id), updated);
      queryClient.invalidateQueries({ queryKey: XERO_PIPELINE_RUNS_QUERY_KEY });
      toast(
        waiting
          ? { title: "Removed from the queue", description: "This run will not start." }
          : {
              title: "Cancellation requested",
              description: "Fabric stops the run asynchronously; the status updates on the next poll.",
            },
      );
    },
    onError: (e) => {
      toast({
        variant: "destructive",
        title: "Couldn’t cancel the run",
        description: (e as Error).message,
      });
    },
  });

  const { summary } = run;
  const rows = run.organisations;

  return (
    <section className="space-y-4 rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <TriggerStatusBadge status={run.status} />
            <span className="text-sm font-medium text-foreground">{MODE_LABEL[run.mode]} run</span>
            <XeroMetricInfo title={`${MODE_LABEL[run.mode]} run`} metrics={METRICS} />
            {active && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="font-mono">{run.run_id}</span>
            <span aria-hidden>·</span>
            <span>Triggered {formatWhen(run.triggered_at)}</span>
            {run.finished_at && (
              <>
                <span aria-hidden>·</span>
                <span>Finished {formatWhen(run.finished_at)}</span>
              </>
            )}
            {run.tenant_ids.length > 0 && (
              <>
                <span aria-hidden>·</span>
                <span>
                  {run.tenant_ids.length} {run.tenant_ids.length === 1 ? "organisation" : "organisations"} selected
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isFetching}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {/* Only the person who started (or queued) a run may cancel it;
              the API says whether that is you. */}
          {active && run.can_cancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending || (!waiting && !run.fabric_job_instance_id)}
              className="gap-2 text-destructive hover:text-destructive"
            >
              {cancel.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
              {waiting ? "Remove from queue" : "Cancel"}
            </Button>
          )}
        </div>
      </div>

      {waiting && (
        <div className="flex items-start gap-2 rounded-md border border-violet-200 bg-violet-50 p-3 text-xs text-violet-800">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Queued behind the run already in progress. It starts automatically as soon as
            that run finishes.
          </span>
        </div>
      )}

      {/* Not shown before Fabric acknowledges the run: can_cancel is false
          for everyone then, the starter included. */}
      {inFlight && run.fabric_job_instance_id && !run.can_cancel && (
        <p className="text-xs text-muted-foreground">
          Only the person who started this run can cancel it.
        </p>
      )}

      {run.poll_error && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Fabric status could not be read: {run.poll_error} Showing the last known state; the
            organisation rows below are current.
          </span>
        </div>
      )}

      {run.failure_reason && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{run.failure_reason}</span>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-5">
        {/* `granted` counts every organisation Xero grants; `organisations`
            counts only those that reported. They differ while a run is in
            flight, and after one that stopped early. */}
        <Stat label="Organisations" value={summary.granted || summary.organisations} />
        <Stat label="Succeeded" value={summary.succeeded} tone="text-emerald-700" />
        <Stat label="Failed" value={summary.failed} tone={summary.failed > 0 ? "text-red-700" : undefined} />
        <Stat label="In progress" value={summary.started} tone={summary.started > 0 ? "text-amber-700" : undefined} />
        <Stat label="Rows" value={summary.rows_ingested} />
      </dl>

      {summary.stopped_early && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            An organisation spent its daily Xero call budget, so the run stopped before
            reaching the rest. Nothing is lost — the next run resumes from each watermark.
          </span>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {waiting
            ? "This run has not started yet. Organisations appear here once it does."
            : inFlight
              ? "Waiting for the notebook to report. Organisations appear here as each one starts."
              : "No organisations were reported for this run."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead>Organisation</TableHead>
                <TableHead className="w-[110px]">Status</TableHead>
                <TableHead className="w-[170px]">Started</TableHead>
                <TableHead className="w-[170px]">Finished</TableHead>
                <TableHead className="w-[100px] text-right">Rows</TableHead>
                <TableHead className="w-[90px] text-right">Pages</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.tenant_id}>
                  <TableCell className="align-top">
                    <div className="font-medium text-foreground">
                      {orgName(row.tenant_id, row.tenant_name)}
                    </div>
                    {/* `skipped` on its own has only ever meant "zero pages
                        fetched", which cannot tell a quiet organisation from a
                        broken one. The reason is what makes the status useful. */}
                    {row.reason && isNoteworthy(row.reason) && (
                      <p className={`mt-1 text-xs ${REASON_TONE[row.reason] ?? "text-muted-foreground"}`}>
                        {REASON_LABEL[row.reason]}
                      </p>
                    )}
                    {row.error_message && (
                      <p className="mt-1 text-xs text-destructive">{row.error_message}</p>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <StatusBadge status={row.status} />
                  </TableCell>
                  <TableCell className="align-top text-muted-foreground">{formatWhen(row.started_at)}</TableCell>
                  <TableCell className="align-top text-muted-foreground">{formatWhen(row.finished_at)}</TableCell>
                  <TableCell className="align-top text-right text-muted-foreground">
                    {formatNumber(row.rows_ingested)}
                  </TableCell>
                  <TableCell className="align-top text-right text-muted-foreground">
                    {formatNumber(row.pages_fetched)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`text-base font-semibold tabular-nums ${tone ?? "text-foreground"}`}>
        {formatNumber(value)}
      </dd>
    </div>
  );
}
